import React, { useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useParams, useHistory } from 'react-router-dom';

import CreateIcon from '@mui/icons-material/Create';

import ToMainMenuButton from '@ts-core/components/ToMainMenuButton';
import SpeedDialMenu from '@ts-core/components/SpeedDialMenu';

import AssignmentsList, { useAssignmentsList } from '../../components/AssignmentsList';

import { useAssignments, useAssignmentActions } from '../../hooks';
import { Assignment } from '../../types';

const AssignmentsPage = () => {
  const intl = useIntl();
  const history = useHistory();
  const { courseId } = useParams<{ courseId: string }>();
  const { assignments, loading } = useAssignments({ courseId });
  const { onRemoveAssignment, onSendAssignment, onToggleAssignment } = useAssignmentActions({ courseId });
  const { sections, isSectionOpen, onToggleSection } = useAssignmentsList({
    assignments,
  });

  const onSelectAssignment = useCallback(
    (assignment: Assignment) => {
      history.push(`assignments/${assignment.id}/details/answers`);
    },
    [history]
  );

  const onCreateAssignment = useCallback(() => {
    history.push('assignments/new');
  }, [history]);

  const onEditAssignment = useCallback(
    (assignment: Assignment) => {
      history.push(`assignments/${assignment.id}/edit`);
    },
    [history]
  );

  const speedDialOptions = useMemo(
    () => [
      {
        icon: <CreateIcon />,
        title: intl.formatMessage({ id: 'assignment.button.create' }),
        onClick: onCreateAssignment,
      },
    ],
    [onCreateAssignment, intl]
  );

  return (
    <>
      <ToMainMenuButton title={intl.formatMessage({ id: 'assignments' })} />
      <AssignmentsList
        sections={sections}
        loading={loading}
        onToggleSection={onToggleSection}
        isSectionOpen={isSectionOpen}
        onToggleAssignment={onToggleAssignment}
        onRemoveAssignment={onRemoveAssignment}
        onSendAssignment={onSendAssignment}
        onEditAssignment={onEditAssignment}
        onSelectAssignment={onSelectAssignment}
        onCreateAssignment={onCreateAssignment}
      />
      <SpeedDialMenu options={speedDialOptions} />
    </>
  );
};

export default AssignmentsPage;
